import { NetworkStatus } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import {
  SearchQueryOrder,
  SearchQueryRequestedPage,
  StackAccessMode,
  StackSpaceAccessGrantSource,
} from "types/generated";

import {
  SpaceDetailsAccessGrantSource,
  SpaceDetailsAccessGrantedVia,
} from "../components/AccessCells/types";
import { STACKS_WITH_ACCESS_TO_SPACE, STACKS_WITH_ACCESS_TO_SPACE_COUNT } from "./gql";
import { SpaceDetailsStack } from "./types";

const GRANT_SOURCE: Record<StackSpaceAccessGrantSource, SpaceDetailsAccessGrantSource> = {
  [StackSpaceAccessGrantSource.Resident]: SpaceDetailsAccessGrantSource.RESIDENT,
  [StackSpaceAccessGrantSource.Propagated]: SpaceDetailsAccessGrantSource.PROPAGATED,
  [StackSpaceAccessGrantSource.Inherited]: SpaceDetailsAccessGrantSource.INHERITED,
};

type UseSpaceDetailsStacksProps = {
  spaceId: string;
  orderBy: SearchQueryOrder;
  first: number;
  fullTextSearch: string | null;
  mode: StackAccessMode;
};

const useSpaceDetailsStacks = ({
  spaceId,
  orderBy,
  first,
  fullTextSearch,
  mode,
}: UseSpaceDetailsStacksProps) => {
  const [requestedPage, setRequestedPage] = useState<SearchQueryRequestedPage | null>(null);
  const [after, setAfter] = useState<string | null>(null);

  // Cursors don't encode the access mode, so a mode flip must restart pagination.
  const resetKey = `${spaceId}:${orderBy.field}:${orderBy.direction}:${fullTextSearch ?? ""}:${mode}`;
  const [previousResetKey, setPreviousResetKey] = useState(resetKey);

  if (previousResetKey !== resetKey) {
    setPreviousResetKey(resetKey);
    setRequestedPage(null);
    setAfter(null);
  }

  const variables = useMemo(
    () => ({
      space: spaceId,
      input: {
        predicates: [],
        orderBy: [orderBy],
        fullTextSearch,
        first,
        after,
        requestedPage,
      },
      mode,
    }),
    [spaceId, orderBy, first, after, requestedPage, fullTextSearch, mode]
  );

  const { error, loading, data, networkStatus, previousData } = usePolledQuery(
    STACKS_WITH_ACCESS_TO_SPACE,
    {
      variables,
      pollingGroup: PollingIntervalGroups.Lists,
      nextFetchPolicy: "cache-first",
    }
  );

  const { data: countData, previousData: previousCountData } = usePolledQuery(
    STACKS_WITH_ACCESS_TO_SPACE_COUNT,
    {
      variables: { space: spaceId, input: { fields: [], fullTextSearch, predicates: [] }, mode },
      pollingGroup: PollingIntervalGroups.Lists,
      nextFetchPolicy: "cache-first",
    }
  );

  const loadStacks = useCallback(
    ({
      cursor,
      requestedPage,
    }: {
      cursor: string | null;
      requestedPage: SearchQueryRequestedPage | null;
    }) => {
      setAfter(cursor);
      setRequestedPage(requestedPage);
    },
    []
  );

  const initialLoading =
    loading && networkStatus === NetworkStatus.loading && !data?.stacksWithAccessToSpace;

  const loadingContent =
    loading &&
    (networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.fetchMore ||
      networkStatus === NetworkStatus.setVariables);

  const stacks = useMemo<SpaceDetailsStack[]>(() => {
    const edges =
      data?.stacksWithAccessToSpace?.edges || previousData?.stacksWithAccessToSpace?.edges || [];

    return edges.map(({ node }) => ({
      id: node.stack.id,
      name: node.stack.name,
      state: node.stack.state,
      labels: node.stack.labels,
      roleBindings: node.roles.map((grant) => {
        const grantedVia: SpaceDetailsAccessGrantedVia = {
          kind: "space",
          id: grant.space.id,
          name: grant.space.name,
          source: GRANT_SOURCE[grant.source],
        };

        // The backend dedupes grants on (role, space) per stack, so this key is unique.
        return {
          id: `${grant.role.id}:${grant.space.id}`,
          role: { id: grant.role.id, name: grant.role.name, isSystem: grant.role.isSystem },
          grantedVia,
        };
      }),
    }));
  }, [data?.stacksWithAccessToSpace?.edges, previousData?.stacksWithAccessToSpace?.edges]);

  const totalCount =
    countData?.stacksWithAccessToSpaceSuggestions.filteredCount ??
    previousCountData?.stacksWithAccessToSpaceSuggestions.filteredCount ??
    0;

  return {
    loadStacks,
    error,
    stacks,
    initialLoading,
    loadingContent,
    totalCount,
    endCursor: data?.stacksWithAccessToSpace?.pageInfo.endCursor,
    startCursor: data?.stacksWithAccessToSpace?.pageInfo.startCursor,
    paginationResetKey: resetKey,
  };
};

export default useSpaceDetailsStacks;
